/**
 * Timeline day grouping.
 *
 * Maps items onto the group headers used by the activity timeline: "Today",
 * "Yesterday", "Earlier this month" and — for anything from a previous month
 * or scheduled in the future — the month the item falls into ("June 2026").
 *
 * All comparisons use the client's local timezone via date-fns defaults, which
 * is intentional: "today" should reflect the user's local calendar date, not
 * UTC. UTC timestamps from the server are parsed via `parseISO` and compared
 * consistently on both sides using the same local-timezone baseline.
 *
 * The grouping keeps the order in which the items arrive, so an ascending and a
 * descending list both stay sorted. Items are expected to be pre-sorted by the
 * same date that `getDate` returns.
 *
 * `now` is injectable ONLY for deterministic tests (fixed clock). Production
 * always uses the default `new Date()`.
 */

import {
  format,
  isSameDay,
  isSameMonth,
  isValid,
  parseISO,
  startOfDay,
  subDays,
} from 'date-fns'
import { TFunction } from 'i18next'

import { formatMonthAndYear } from './formatDate'

export interface TimelineDateGroup<T> {
  /**
   * Group identity: `today`, `yesterday`, `earlierThisMonth`, `undated`, or
   * `yyyy-MM` for month groups. Unique within a single result, so it is safe
   * as React key.
   */
  id: string
  /** Localized group header. */
  label: string
  items: T[]
}

interface GroupItemsByTimelineDateArgs<T> {
  items: T[]
  /**
   * Date an item is grouped by. Items without a usable date are collected in a
   * trailing group instead of being dropped.
   */
  getDate: (item: T) => Date | undefined
  t: TFunction
  now?: Date
}

/**
 * Converts a PISA date attribute — an ISO string, sometimes absent — into a
 * `Date`.
 *
 * @returns the parsed date, or `undefined` if the value is missing or unparsable
 */
export const parseDateAttribute = (value: unknown): Date | undefined => {
  if (value instanceof Date) return isValid(value) ? value : undefined
  if (typeof value !== 'string' || !value) return undefined

  const date = parseISO(value)
  return isValid(date) ? date : undefined
}

const resolveGroup = (
  date: Date | undefined,
  now: Date,
  t: TFunction,
): Pick<TimelineDateGroup<never>, 'id' | 'label'> => {
  if (!date) return { id: 'undated', label: t('timeline.group.undated') }

  if (isSameDay(date, now)) {
    return { id: 'today', label: t('timeline.group.today') }
  }
  if (isSameDay(date, subDays(now, 1))) {
    return { id: 'yesterday', label: t('timeline.group.yesterday') }
  }

  // Items in the current month that fall before today. Future items in the
  // same month (planned activities) have date >= startOfDay(now) and fall
  // through to the month bucket below.
  if (isSameMonth(date, now) && date < startOfDay(now)) {
    return {
      id: 'earlierThisMonth',
      label: t('timeline.group.earlierThisMonth'),
    }
  }

  return { id: format(date, 'yyyy-MM'), label: formatMonthAndYear(date) }
}

/**
 * Groups items by the timeline day group their date falls into.
 *
 * @returns one group per distinct day group, in first-seen order; empty when
 *          `items` is empty
 */
export const groupItemsByTimelineDate = <T>({
  items,
  getDate,
  t,
  now = new Date(),
}: GroupItemsByTimelineDateArgs<T>): TimelineDateGroup<T>[] => {
  const groups = new Map<string, TimelineDateGroup<T>>()

  items.forEach((item) => {
    const { id, label } = resolveGroup(getDate(item), now, t)
    const group = groups.get(id)

    if (group) {
      group.items.push(item)
    } else {
      groups.set(id, { id, label, items: [item] })
    }
  })

  return [...groups.values()]
}
